import { useState, useEffect, useCallback } from "react";
import { api, toast } from "../App";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ChevronLeft, ChevronRight, Sparkles, AlertTriangle, Plus, ChevronDown, Check, Pencil, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from "@/components/ui/command";
import { fh } from "@/utils/hours";

const SKILL_LEVELS = [
  { value: "", label: "Not set" },
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
  { value: "expert", label: "Expert" },
];
const SKILL_COLORS = {
  beginner: "bg-gray-200 text-gray-700",
  intermediate: "bg-blue-100 text-blue-700",
  advanced: "bg-green-100 text-green-700",
  expert: "bg-purple-100 text-purple-700",
};

// Parse YYYY-MM-DD as a LOCAL date (avoids JS's UTC parsing which shifts the
// weekday in west-of-UTC browsers — e.g. "2026-06-01" was rendering as Sunday
// instead of Monday because `new Date("2026-06-01")` → UTC midnight → Sun in PT).
function localDateFromISO(s) {
  if (!s || typeof s !== "string" || s.length < 10) return new Date(s);
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export default function ResourceBoard({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [days] = useState(14);
  const [search, setSearch] = useState("");
  const [draggingTask, setDraggingTask] = useState(null);
  const [hoverCell, setHoverCell] = useState(null);  // {userId, date}
  const [skillsUser, setSkillsUser] = useState(null);  // For skills modal
  const [skillsForm, setSkillsForm] = useState({ modeling: "", detailing: "", erection: "", checker_eligible: false });
  const [skillsSaving, setSkillsSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [teamOnly, setTeamOnly] = useState(false);

  // Add-Task dialog state (inline task creation from the resource board grid).
  // Same POST endpoint Project Planning uses → tasks created here appear there too.
  const [addTask, setAddTask] = useState(null);     // {user_id, user_name, date} or null
  const [allProjects, setAllProjects] = useState([]); // dropdown options
  const [taskForm, setTaskForm] = useState({
    project_id: "", phase_id: "", scope_id: "", activity_id: "",
    description: "", allocated_hours: "",
  });
  const [taskPhases, setTaskPhases] = useState([]);
  const [taskScopes, setTaskScopes] = useState([]);
  const [taskActivities, setTaskActivities] = useState([]);
  const [taskSaving, setTaskSaving] = useState(false);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);

  // Edit-Task dialog state
  const [editTask, setEditTask] = useState(null);
  const [editForm, setEditForm] = useState({ description: "", allocated_hours: "", planned_start_date: "", due_date: "" });
  const [editSaving, setEditSaving] = useState(false);

  const isManagement = user?.access_level === "Admin" || user?.department === "Management" || user?.department === "HR";

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ start_date: startDate, days: String(days) });
      if (projectId) params.set("project_id", projectId);
      if (projectId && teamOnly) params.set("team_only", "true");
      const res = await api.get(`/planning/resource-board?${params.toString()}`);
      setData(res.data);
    } catch {
      toast.error("Failed to load resource board");
    } finally {
      setLoading(false);
    }
  }, [startDate, days, projectId, teamOnly]);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  // Load project list for the filter
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/projects`);
        const list = (Array.isArray(res.data) ? res.data : (res.data?.projects || []))
          .filter(p => p && p.id)
          .sort((a, b) => (a.project_number || "").localeCompare(b.project_number || ""));
        setProjects(list);
      } catch {
        // silent
      }
    })();
  }, []);

  const shiftWeek = (delta) => {
    const d = localDateFromISO(startDate);
    d.setDate(d.getDate() + delta * 7);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    setStartDate(`${y}-${m}-${dd}`);
  };

  // -------- Inline Add-Task (cascading Project → Phase → Scope → Activity) --------
  const openAddTaskDialog = async (userId, userName, date) => {
    setAddTask({ user_id: userId, user_name: userName, date });
    setTaskForm({ project_id: "", phase_id: "", scope_id: "", activity_id: "", description: "", allocated_hours: "" });
    setTaskPhases([]); setTaskScopes([]); setTaskActivities([]);
    // Lazily load all active projects sorted desc by project_number.
    // (The strict `planning_initialized=true` flag isn't set on legacy projects;
    // we instead surface them all and the cascading Phase/Scope/Activity dropdowns
    // naturally show empty for ones without a planning skeleton yet.)
    try {
      const res = await api.get(`/projects`);
      const list = (Array.isArray(res.data) ? res.data : (res.data?.projects || []))
        .filter(p => p && p.id && (p.status === undefined || p.status !== "cancelled"))
        .sort((a, b) => (b.project_number || "").localeCompare(a.project_number || "", undefined, { numeric: true, sensitivity: "base" }));
      setAllProjects(list);
    } catch {
      setAllProjects([]);
    }
  };

  // When a project is picked, fetch its planning phases + scopes + activities.
  const onProjectPick = async (pid) => {
    setTaskForm(f => ({ ...f, project_id: pid, phase_id: "", scope_id: "", activity_id: "" }));
    setTaskPhases([]); setTaskScopes([]); setTaskActivities([]);
    if (!pid) return;
    try {
      const [phasesRes, actRes] = await Promise.all([
        api.get(`/projects/${pid}/planning/phases`),
        api.get(`/projects/${pid}/planning/activities`),
      ]);
      setTaskPhases(phasesRes.data?.phases || phasesRes.data || []);
      setTaskActivities(actRes.data?.activities || []);
    } catch {
      toast.error("Failed to load project planning data");
    }
  };

  // When phase changes → derive scopes from phase.scope_allocations
  const onPhasePick = (phaseId) => {
    setTaskForm(f => ({ ...f, phase_id: phaseId, scope_id: "", activity_id: "" }));
    const phase = taskPhases.find(p => p.id === phaseId);
    setTaskScopes(phase?.scope_allocations || []);
  };

  // Scope changes → activities filtered by scope_allocation_id + phase
  const filteredActivities = taskActivities.filter(a =>
    a.phase_id === taskForm.phase_id &&
    a.scope_allocation_id === taskForm.scope_id
  );

  const saveAddTask = async () => {
    if (!taskForm.activity_id || !taskForm.description || !taskForm.allocated_hours) {
      toast.error("Pick activity, enter description and hours");
      return;
    }
    setTaskSaving(true);
    try {
      const body = {
        description: taskForm.description,
        allocated_hours: parseFloat(taskForm.allocated_hours),
        assigned_to: addTask.user_id,
        planned_start_date: addTask.date,
        due_date: addTask.date,
        auto_pair: true,            // Auto-pair Checker for modeling/detailing/erection
        confirm_over_leave: false,  // First attempt — backend may 409 with leave-conflict
      };
      try {
        await api.post(`/planning/activities/${taskForm.activity_id}/tasks`, body);
      } catch (err) {
        // Mirror Project Planning's leave-conflict UX
        if (err.response?.status === 409 && err.response.data?.detail?.code === "leave_conflict") {
          const lv = (err.response.data.detail.leaves || [])[0];
          const confirm = window.confirm(
            `${addTask.user_name} has an ${lv?.status || "approved"} leave on ${lv?.start_date}…${lv?.end_date} (${lv?.leave_type || "leave"}). Proceed anyway?`
          );
          if (!confirm) return;
          await api.post(`/planning/activities/${taskForm.activity_id}/tasks`, { ...body, confirm_over_leave: true });
        } else {
          throw err;
        }
      }
      toast.success("Task added — also visible in Project Planning");
      setAddTask(null);
      await fetchBoard();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Failed to add task");
    } finally {
      setTaskSaving(false);
    }
  };

  // -------- Edit Task (click any chip on the grid to edit) --------
  const openEditTask = (card) => {
    setEditTask(card);
    setEditForm({
      description: card.description || "",
      allocated_hours: String(card.allocated_hours || ""),
      planned_start_date: card.planned_start_date || "",
      due_date: card.due_date || "",
    });
  };
  const saveEditTask = async () => {
    if (!editTask) return;
    setEditSaving(true);
    try {
      const body = {
        activity_id: editTask.activity_id,
        description: editForm.description,
        allocated_hours: parseFloat(editForm.allocated_hours),
        planned_start_date: editForm.planned_start_date || null,
        due_date: editForm.due_date || null,
        confirm_over_leave: false,
      };
      try {
        await api.put(`/planning/tasks/${editTask.id}`, body);
      } catch (err) {
        if (err.response?.status === 409 && err.response.data?.detail?.code === "leave_conflict") {
          const lv = (err.response.data.detail.leaves || [])[0];
          const confirm = window.confirm(
            `Assignee has an ${lv?.status || "approved"} leave on ${lv?.start_date}…${lv?.end_date}. Proceed anyway?`
          );
          if (!confirm) return;
          await api.put(`/planning/tasks/${editTask.id}`, { ...body, confirm_over_leave: true });
        } else {
          throw err;
        }
      }
      toast.success("Task updated");
      setEditTask(null);
      await fetchBoard();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Failed to update task");
    } finally {
      setEditSaving(false);
    }
  };
  const deleteEditTask = async () => {
    if (!editTask) return;
    if (!window.confirm(`Delete task "${editTask.description}"? This cannot be undone.`)) return;
    setEditSaving(true);
    try {
      await api.delete(`/planning/tasks/${editTask.id}?activity_id=${editTask.activity_id}`);
      toast.success("Task deleted");
      setEditTask(null);
      await fetchBoard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete task");
    } finally {
      setEditSaving(false);
    }
  };

  // Drag handlers
  const onDragStart = (e, task, sourceUserId, sourceDate) => {
    setDraggingTask({ ...task, _sourceUserId: sourceUserId, _sourceDate: sourceDate });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", task.id);
  };
  const onDragOver = (e, userId, date) => {
    e.preventDefault();
    setHoverCell(`${userId}|${date}`);
  };
  const onDragLeave = () => setHoverCell(null);
  
  const handleDrop = async (e, targetUserId, targetDate) => {
    e.preventDefault();
    setHoverCell(null);
    if (!draggingTask) return;
    const task = draggingTask;
    if (task._sourceUserId === targetUserId && task._sourceDate === targetDate) {
      setDraggingTask(null);
      return;
    }
    // Soft warning for skill mismatch (checker dropped on non-checker-eligible user)
    const targetUser = (data?.users || []).find(u => u.id === targetUserId);
    if (task.role === "checker" && targetUser && targetUser.skills && targetUser.skills.checker_eligible === false) {
      if (!confirm(`${targetUser.name} is not marked checker-eligible. Assign anyway?`)) {
        setDraggingTask(null);
        return;
      }
    }
    // Soft warning for capacity overload
    const cell = data?.assignments?.[targetUserId]?.[targetDate] || [];
    const used = cell.reduce((s, c) => s + (c.allocated_hours || 0), 0);
    const cap = targetUser?.daily_capacity || 8;
    if (used + (task.allocated_hours || 0) > cap) {
      if (!confirm(`This drop will put ${targetUser?.name} at ${used + task.allocated_hours}h on ${targetDate} (capacity ${cap}h). Continue?`)) {
        setDraggingTask(null);
        return;
      }
    }
    try {
      await api.put(`/planning/tasks/${task.id}/assign`, {
        assigned_to: targetUserId === "_unassigned" ? null : targetUserId,
        planned_start_date: targetUserId === "_unassigned" ? null : targetDate,
      });
      toast.success("Task moved");
      fetchBoard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to move task");
    } finally {
      setDraggingTask(null);
    }
  };

  // Capacity color
  const cellCapacityColor = (cellHours, cap) => {
    const pct = cap ? (cellHours / cap) * 100 : 0;
    if (pct <= 80) return "bg-green-50";
    if (pct <= 100) return "bg-amber-50";
    return "bg-red-50";
  };
  const cellBorderColor = (cellHours, cap) => {
    const pct = cap ? (cellHours / cap) * 100 : 0;
    if (pct <= 80) return "border-green-200";
    if (pct <= 100) return "border-amber-300";
    return "border-red-400";
  };

  // Open skills modal
  const openSkills = (u) => {
    setSkillsUser(u);
    const s = u.skills || {};
    setSkillsForm({
      modeling: s.modeling || "",
      detailing: s.detailing || "",
      erection: s.erection || "",
      checker_eligible: !!s.checker_eligible,
    });
  };
  const saveSkills = async () => {
    if (!skillsUser) return;
    setSkillsSaving(true);
    try {
      await api.put(`/users/${skillsUser.id}/skills`, skillsForm);
      toast.success("Skills updated");
      setSkillsUser(null);
      fetchBoard();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSkillsSaving(false);
    }
  };

  // Filter cards by search
  const matches = (c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (c.description || "").toLowerCase().includes(q)
      || (c.project_number || "").toLowerCase().includes(q)
      || (c.project_name || "").toLowerCase().includes(q);
  };

  if (loading && !data) {
    return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-md3-primary" /></div>;
  }

  const users = data?.users || [];
  const dates = data?.dates || [];

  return (
    <div className="space-y-4">
      {/* Top controls */}
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div className="flex gap-2 items-center flex-wrap">
          <Button variant="outline" size="sm" onClick={() => shiftWeek(-1)} data-testid="rboard-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-40 bg-white" data-testid="rboard-start" />
          <Button variant="outline" size="sm" onClick={() => shiftWeek(1)} data-testid="rboard-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setStartDate(new Date().toISOString().slice(0, 10))}>Today</Button>
          <Select value={projectId || "_all"} onValueChange={(v) => { setProjectId(v === "_all" ? "" : v); if (v === "_all") setTeamOnly(false); }}>
            <SelectTrigger className="w-56 bg-white" data-testid="rboard-project-select">
              <SelectValue placeholder="All projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All projects</SelectItem>
              {projects.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.project_number} — {p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {projectId && (
            <label className="flex items-center gap-1.5 text-xs bg-white border rounded px-2 py-1.5 cursor-pointer" data-testid="rboard-team-only-label">
              <input
                type="checkbox"
                checked={teamOnly}
                onChange={(e) => setTeamOnly(e.target.checked)}
                data-testid="rboard-team-only-toggle"
              />
              <span>Project team only</span>
            </label>
          )}
        </div>
        <Input placeholder="Search tasks..." value={search} onChange={e => setSearch(e.target.value)} className="w-72 bg-white" data-testid="rboard-search" />
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 text-xs text-md3-on-surface-variant">
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-100 border border-green-300 rounded inline-block" />≤ 80%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-100 border border-amber-300 rounded inline-block" />80–100%</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-100 border border-red-400 rounded inline-block" />Over 100%</span>
        <span className="ml-auto">Drag tasks to assign or reschedule. Drop onto "Unassigned" to remove.</span>
      </div>

      {/* Grid */}
      <div className="overflow-auto bg-md3-surface-container rounded-md3-lg p-2">
        <table className="text-xs border-separate border-spacing-1 min-w-full">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-md3-surface-container text-left px-2 py-1 w-44">User</th>
              {dates.map(d => {
                const day = localDateFromISO(d);
                const dow = day.getDay();
                const isWeekend = dow === 0 || dow === 6;
                const holidayName = data?.holidays?.[d];
                const headerCls = holidayName
                  ? "bg-rose-50 text-rose-900"
                  : isWeekend
                  ? "bg-slate-100 text-slate-700"
                  : "";
                return (
                  <th
                    key={d}
                    className={`text-center px-1 py-1 min-w-[120px] ${headerCls}`}
                    title={holidayName || (isWeekend ? (dow === 0 ? "Sunday" : "Saturday") : "")}
                    data-testid={`rboard-header-${d}`}
                  >
                    <div className="font-mono text-[10px]">{day.toLocaleDateString(undefined, { weekday: "short" })}</div>
                    <div className="text-[11px]">{d.slice(5)}</div>
                    {holidayName && (
                      <div className="text-[9px] italic truncate" title={holidayName}>{holidayName}</div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {/* Unassigned row */}
            <tr>
              <td className="sticky left-0 z-10 bg-md3-tertiary-container px-2 py-2 font-semibold text-md3-on-tertiary-container">
                Unassigned
              </td>
              <td
                colSpan={dates.length}
                onDragOver={(e) => onDragOver(e, "_unassigned", "_any")}
                onDragLeave={onDragLeave}
                onDrop={(e) => handleDrop(e, "_unassigned", "_any")}
                className={`p-2 bg-md3-tertiary-container/40 border border-dashed border-md3-outline-variant ${hoverCell === "_unassigned|_any" ? "ring-2 ring-md3-primary" : ""}`}
                data-testid="rboard-unassigned"
              >
                <div className="flex flex-wrap gap-2">
                  {(data?.unassigned || []).filter(matches).slice(0, 50).map(c => (
                    <TaskChip key={c.id} c={c} draggable onDragStart={(e) => onDragStart(e, c, "_unassigned", "_any")} onEdit={openEditTask} />
                  ))}
                  {(data?.unassigned || []).filter(matches).length === 0 && (
                    <div className="text-xs italic text-md3-on-surface-variant py-1">No unassigned tasks</div>
                  )}
                </div>
              </td>
            </tr>
            {/* User rows */}
            {users.map(u => {
              const userTotal = dates.reduce((s, d) => s + ((data?.assignments?.[u.id]?.[d] || []).reduce((x, c) => x + (c.allocated_hours || 0), 0)), 0);
              return (
                <tr key={u.id}>
                  <td className="sticky left-0 z-10 bg-md3-surface-container px-2 py-1 border-r border-md3-outline-variant">
                    <div className="font-medium text-sm flex items-center gap-1">
                      {u.name}
                      <button
                        onClick={() => openSkills(u)}
                        className="text-[10px] text-md3-primary hover:underline ml-1"
                        title="Skills"
                        data-testid={`rboard-skills-${u.id}`}
                      >
                        <Sparkles className="h-3 w-3 inline" />
                      </button>
                    </div>
                    <SkillBadges skills={u.skills} />
                    <div className="text-[10px] text-md3-on-surface-variant mt-0.5">Week: {fh(userTotal)}h</div>
                  </td>
                  {dates.map(d => {
                    const cellCards = (data?.assignments?.[u.id]?.[d] || []).filter(matches);
                    const cellHours = cellCards.reduce((s, c) => s + (c.allocated_hours || 0), 0);
                    const isHover = hoverCell === `${u.id}|${d}`;
                    // Leave overlay for this user × date
                    const leavesForCell = (data?.leaves_by_user?.[u.id] || []).filter(lv => lv.start_date <= d && d <= lv.end_date);
                    const onLeaveApproved = leavesForCell.find(lv => lv.status === "approved");
                    const onLeavePending = !onLeaveApproved && leavesForCell.find(lv => lv.status === "pending");
                    const leaveBadge = onLeaveApproved || onLeavePending;
                    // Off-day overlay: holiday or weekend. Tint priority: leave > holiday > weekend > capacity heat.
                    const dow = localDateFromISO(d).getDay();
                    const isWeekend = dow === 0 || dow === 6;
                    const holidayName = data?.holidays?.[d];
                    const offBg = onLeaveApproved
                      ? "bg-amber-50/60"
                      : holidayName
                      ? "bg-rose-50/80"
                      : isWeekend
                      ? "bg-slate-100/80"
                      : "";
                    return (
                      <td
                        key={d}
                        onDragOver={(e) => onDragOver(e, u.id, d)}
                        onDragLeave={onDragLeave}
                        onDrop={(e) => handleDrop(e, u.id, d)}
                        className={`p-1 align-top border ${cellBorderColor(cellHours, u.daily_capacity)} ${offBg || cellCapacityColor(cellHours, u.daily_capacity)} rounded ${isHover ? "ring-2 ring-md3-primary" : ""} min-h-[60px]`}
                        title={holidayName || (isWeekend ? (dow === 0 ? "Sunday" : "Saturday") : "")}
                        data-testid={`rboard-cell-${u.id}-${d}`}
                      >
                        <div className="text-[10px] font-semibold mb-0.5 flex items-center justify-between">
                          <span className={cellHours > u.daily_capacity ? "text-red-700" : "text-md3-on-surface-variant"}>
                            {fh(cellHours)}h / {fh(u.daily_capacity)}h
                          </span>
                          <div className="flex items-center gap-1">
                            {cellHours > u.daily_capacity && <AlertTriangle className="h-3 w-3 text-red-600" />}
                            <button
                              type="button"
                              onClick={() => openAddTaskDialog(u.id, u.name, d)}
                              className="opacity-40 hover:opacity-100 text-md3-on-surface-variant hover:text-md3-primary transition-opacity"
                              title="Add task on this date"
                              data-testid={`rboard-add-task-${u.id}-${d}`}
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                        {leaveBadge && (
                          <div
                            className={`text-[9px] font-bold px-1 py-0.5 mb-1 rounded text-center ${
                              onLeaveApproved ? "bg-amber-200 text-amber-900" : "bg-amber-100 text-amber-800 border border-dashed border-amber-300"
                            }`}
                            title={`${leaveBadge.leave_type || "Leave"}${leaveBadge.is_half_day ? ` (Half ${leaveBadge.half_day_period || ""})` : ""}${leaveBadge.status === "pending" ? " · Pending" : ""}`}
                            data-testid={`rboard-ooo-${u.id}-${d}`}
                          >
                            {leaveBadge.is_half_day ? "½ OOO" : "ON LEAVE"}
                            {leaveBadge.status === "pending" ? "*" : ""}
                          </div>
                        )}
                        <div className="flex flex-col gap-1">
                          {cellCards.map(c => (
                            <TaskChip key={c.id} c={c} draggable onDragStart={(e) => onDragStart(e, c, u.id, d)} onEdit={openEditTask} />
                          ))}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Skills modal */}
      <Dialog open={!!skillsUser} onOpenChange={o => !o && setSkillsUser(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle>Skills — {skillsUser?.name}</DialogTitle>
          </DialogHeader>
          {!isManagement && skillsUser?.id !== user?.id ? (
            <div className="text-sm text-md3-on-surface-variant">View only — only the user themselves or Management can edit skills.</div>
          ) : null}
          <div className="space-y-3">
            {["modeling", "detailing", "erection"].map(skill => (
              <div key={skill}>
                <label className="text-xs font-medium capitalize block mb-1">{skill}</label>
                <Select value={skillsForm[skill] || "_unset"} onValueChange={(v) => setSkillsForm(f => ({ ...f, [skill]: v === "_unset" ? "" : v }))}>
                  <SelectTrigger className="bg-white" data-testid={`skill-${skill}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_unset">Not set</SelectItem>
                    {SKILL_LEVELS.filter(l => l.value).map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={skillsForm.checker_eligible} onChange={(e) => setSkillsForm(f => ({ ...f, checker_eligible: e.target.checked }))} data-testid="skill-checker-eligible" />
              Checker eligible
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSkillsUser(null)}>Close</Button>
            {(isManagement || skillsUser?.id === user?.id) && (
              <Button onClick={saveSkills} disabled={skillsSaving} className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary" data-testid="skill-save">
                {skillsSaving ? "Saving..." : "Save"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Task dialog — inline task creation from the resource board grid. */}
      <Dialog open={!!addTask} onOpenChange={(o) => !o && setAddTask(null)}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
            <div className="text-sm text-md3-on-surface-variant mt-1">
              {addTask?.user_name} · {addTask?.date}
            </div>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm font-medium">Project *</Label>
              {(() => {
                const selected = allProjects.find(p => p.id === taskForm.project_id);
                return (
                  <Popover open={projectPickerOpen} onOpenChange={setProjectPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between font-normal"
                        data-testid="add-task-project"
                      >
                        <span className={selected ? "" : "text-md3-on-surface-variant"}>
                          {selected ? `${selected.project_number || ""} — ${selected.name || ""}` : "Search by # or name…"}
                        </span>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                      <Command
                        filter={(value, search) => {
                          if (!search) return 1;
                          return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                        }}
                      >
                        <CommandInput placeholder="Type project # or name…" data-testid="add-task-project-search" />
                        <CommandList>
                          <CommandEmpty>No project matches your search.</CommandEmpty>
                          <CommandGroup>
                            {allProjects.map(p => (
                              <CommandItem
                                key={p.id}
                                value={`${p.project_number || ""}|${p.name || ""}`}
                                onSelect={() => {
                                  onProjectPick(p.id);
                                  setProjectPickerOpen(false);
                                }}
                                data-testid={`add-task-project-opt-${p.id}`}
                              >
                                <Check className={`mr-2 h-4 w-4 ${taskForm.project_id === p.id ? "opacity-100" : "opacity-0"}`} />
                                <span className="font-mono text-md3-body-small">{p.project_number || "—"}</span>
                                <span className="mx-2 text-md3-on-surface-variant">·</span>
                                <span className="truncate">{p.name || ""}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                );
              })()}
            </div>
            <div>
              <Label className="text-sm font-medium">Phase *</Label>
              <Select value={taskForm.phase_id} onValueChange={onPhasePick} disabled={!taskForm.project_id}>
                <SelectTrigger data-testid="add-task-phase"><SelectValue placeholder="Select phase" /></SelectTrigger>
                <SelectContent>
                  {taskPhases.map(p => (
                    <SelectItem key={p.id} value={p.id}>{(p.name || p.phase_type || "").replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Scope *</Label>
              <Select
                value={taskForm.scope_id}
                onValueChange={(v) => setTaskForm(f => ({ ...f, scope_id: v, activity_id: "" }))}
                disabled={!taskForm.phase_id}
              >
                <SelectTrigger data-testid="add-task-scope"><SelectValue placeholder="Select scope" /></SelectTrigger>
                <SelectContent>
                  {taskScopes.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.scope_name || s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Activity *</Label>
              <Select
                value={taskForm.activity_id}
                onValueChange={(v) => setTaskForm(f => ({ ...f, activity_id: v }))}
                disabled={!taskForm.scope_id}
              >
                <SelectTrigger data-testid="add-task-activity"><SelectValue placeholder="Select activity" /></SelectTrigger>
                <SelectContent>
                  {filteredActivities.map(a => {
                    const remaining = (a.allocated_hours || 0) - ((a.tasks || []).reduce((s, t) => s + (t.allocated_hours || 0), 0));
                    return (
                      <SelectItem key={a.id} value={a.id}>
                        {(a.activity_type || a.name || "").replace(/_/g, " ")} · {fh(remaining)}h left
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Task Description *</Label>
              <Input
                value={taskForm.description}
                onChange={(e) => setTaskForm(f => ({ ...f, description: e.target.value }))}
                placeholder="What needs to be done"
                data-testid="add-task-description"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Hours *</Label>
              <Input
                type="number"
                step="0.5"
                value={taskForm.allocated_hours}
                onChange={(e) => setTaskForm(f => ({ ...f, allocated_hours: e.target.value }))}
                placeholder="e.g. 8"
                data-testid="add-task-hours"
              />
              <p className="text-xs text-md3-on-surface-variant mt-1">
                Auto-pairs a Checker for Modeling / Detailing / Erection activities.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddTask(null)}>Cancel</Button>
            <Button
              onClick={saveAddTask}
              disabled={taskSaving}
              className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary"
              data-testid="add-task-save"
            >
              {taskSaving ? "Saving..." : "Save Task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Task dialog — click any task chip on the grid to edit / delete. */}
      <Dialog open={!!editTask} onOpenChange={(o) => !o && setEditTask(null)}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
            <div className="text-sm text-md3-on-surface-variant mt-1">
              {editTask?.project_number} · {(editTask?.activity_type || "").replace(/_/g, " ")}
            </div>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm font-medium">Description *</Label>
              <Input
                value={editForm.description}
                onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
                data-testid="edit-task-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Hours *</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={editForm.allocated_hours}
                  onChange={(e) => setEditForm(f => ({ ...f, allocated_hours: e.target.value }))}
                  data-testid="edit-task-hours"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Planned Start</Label>
                <Input
                  type="date"
                  value={editForm.planned_start_date}
                  onChange={(e) => setEditForm(f => ({ ...f, planned_start_date: e.target.value }))}
                  data-testid="edit-task-start"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Due Date</Label>
              <Input
                type="date"
                value={editForm.due_date}
                onChange={(e) => setEditForm(f => ({ ...f, due_date: e.target.value }))}
                data-testid="edit-task-due"
              />
            </div>
          </div>
          <DialogFooter className="flex !justify-between">
            <Button
              variant="outline"
              onClick={deleteEditTask}
              disabled={editSaving}
              className="text-red-700 hover:bg-red-50 border-red-300"
              data-testid="edit-task-delete"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditTask(null)}>Cancel</Button>
              <Button
                onClick={saveEditTask}
                disabled={editSaving}
                className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary"
                data-testid="edit-task-save"
              >
                <Pencil className="h-4 w-4 mr-1" />
                {editSaving ? "Saving..." : "Save"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaskChip({ c, draggable, onDragStart, onEdit }) {
  const overdue = c.due_date && c.due_date < new Date().toISOString().slice(0, 10);
  const isDone = c.status === "done" || c.workflow_state === "done";
  const borderCls = isDone
    ? "border-emerald-300 bg-emerald-50/70"
    : overdue
    ? "border-red-400 bg-white"
    : "border-md3-outline-variant bg-white";
  return (
    <div
      draggable={draggable && !isDone}
      onDragStart={onDragStart}
      onClick={(e) => {
        // Plain left-click (no drag) opens the edit dialog.
        if (e.button === 0 && onEdit) onEdit(c);
      }}
      className={`border ${borderCls} rounded px-1.5 py-1 text-[11px] shadow-sm cursor-pointer hover:shadow-md hover:border-md3-primary transition-shadow ${isDone ? "opacity-75" : ""}`}
      title={`${c.project_number} ${c.description} — ${fh(c.allocated_hours)}h${isDone ? " · DONE" : ""} · click to edit${isDone ? "" : ", drag to move"}`}
      data-testid={`rboard-chip-${c.id}`}
    >
      <div className="font-mono text-[9px] text-md3-primary truncate">{c.project_number}</div>
      <div className={`font-medium truncate max-w-[120px] ${isDone ? "line-through text-md3-on-surface-variant" : ""}`} title={c.description}>{c.description}</div>
      <div className="flex justify-between items-center text-[10px] mt-0.5">
        <span className="text-md3-on-surface-variant">{fh(c.allocated_hours)}h</span>
        {isDone
          ? <Badge variant="outline" className="text-[8px] px-1 py-0 border-emerald-400 text-emerald-700">done</Badge>
          : c.role && <Badge variant="outline" className="text-[8px] px-1 py-0">{c.role}</Badge>}
      </div>
    </div>
  );
}

function SkillBadges({ skills }) {
  if (!skills || (!skills.modeling && !skills.detailing && !skills.erection && !skills.checker_eligible)) {
    return null;
  }
  return (
    <div className="flex flex-wrap gap-0.5 mt-0.5">
      {skills.modeling && <span className={`px-1 rounded text-[9px] ${SKILL_COLORS[skills.modeling]}`}>M:{skills.modeling[0].toUpperCase()}</span>}
      {skills.detailing && <span className={`px-1 rounded text-[9px] ${SKILL_COLORS[skills.detailing]}`}>D:{skills.detailing[0].toUpperCase()}</span>}
      {skills.erection && <span className={`px-1 rounded text-[9px] ${SKILL_COLORS[skills.erection]}`}>E:{skills.erection[0].toUpperCase()}</span>}
      {skills.checker_eligible && <span className="px-1 rounded text-[9px] bg-teal-100 text-teal-700">CHK</span>}
    </div>
  );
}
